<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {ConsumerProduct} from '@/consumer/interfaces';
import {VidalInteraction} from '@/drug-interaction/interfaces';
import {consumerProductApi, drugInteractionApi} from '@/container';
import BaseButton from '@/core/components/base/BaseButton.vue';
import BaseFlexSpinner from '@/core/components/base/spinner/BaseFlexSpinner.vue';
import InteractionGroupList from '@/drug-interaction/components/alerts/InteractionGroupList.vue';
import {
    type InteractionItem,
    interactionKey,
    toPlainText,
    useInteractionGroups
} from '@/drug-interaction/composables/useInteractionGroups';

const emits = defineEmits<{ (e: 'previous'): void; (e: 'saved'): void }>();

const props = defineProps<{
    customerId: number;
    products: ConsumerProduct[];
}>();

const loading = ref(false);
const saving = ref(false);
const error = ref<string>();
const interactions = ref<VidalInteraction[]>();

const toItem = (consumerProduct: ConsumerProduct): InteractionItem => ({
    id: consumerProduct.id,
    code: consumerProduct.product.cip,
    name: consumerProduct.product.name,
});

const {checkedKeys, comments, toggleCheck, groupedByItem, findRelatedItemId} = useInteractionGroups(
    () => interactions.value,
    () => props.products.map(toItem)
);

onMounted(async () => {
    loading.value = true;
    try {
        interactions.value = await drugInteractionApi().interactions(
            props.products.map(p => p.product.cip),
            props.customerId
        );
    } catch {
        error.value = 'Une erreur est survenue lors de la récupération des données d\'interaction.';
    } finally {
        loading.value = false;
    }
});

const save = async (): Promise<void> => {
    const payload = {
        alerts: groupedByItem.value.flatMap(({item, interactions}) => {
            const checked = interactions
                .filter(interaction => checkedKeys.value.has(interactionKey(interaction)));

            const interactionAlerts = checked.map(interaction => ({
                consumer_product_id: item.id,
                reference_consumer_product_id: findRelatedItemId([interaction], item.id),
                content: toPlainText(interaction.content ?? ''),
            }));

            const commentAlert = comments.value[item.id]
                ? [{
                    consumer_product_id: item.id,
                    reference_consumer_product_id: null,
                    content: comments.value[item.id],
                }]
                : [];

            return [...interactionAlerts, ...commentAlert];
        }),
    };

    saving.value = true;
    return consumerProductApi().createAlertReview(
        `c_${props.customerId}`,
        payload
    )
        .then(() => emits('saved'))
        .finally(() => saving.value = false);
};
</script>

<template>
  <div class="flex flex-col gap-2 flex-1 justify-between min-h-0">
    <BaseFlexSpinner v-if="loading" />

    <p v-else-if="error">
      {{ error }}
    </p>

    <template v-else-if="interactions">
      <p class="text-xs text-gray-500 shrink-0">
        Sélectionnez les alertes pertinentes à inclure dans l'analyse.
      </p>

      <InteractionGroupList
        :groups="groupedByItem"
        :checked-keys="checkedKeys"
        :comments="comments"
        @toggle="toggleCheck"
        @update:comments="(id, value) => comments[id] = value"
      />
    </template>

    <div class="flex justify-between pt-2 shrink-0">
      <BaseButton @click="$emit('previous')">
        Précédent
      </BaseButton>
      <BaseButton
        :loading="saving"
        class="bg-primary text-white"
        @click="save"
      >
        Enregistrer l'analyse
      </BaseButton>
    </div>
  </div>
</template>
